import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, MouseEvent } from 'react';
import { CheckCircle2, Loader2, Plus, QrCode, RefreshCw, Smartphone } from 'lucide-react';
import { getDeviceFingerprint } from '../../utils/deviceFingerprint';
import {
  createLoginPairingQr,
  cancelLoginPairingSession,
  getLoginPairingStatus,
  type LoginPairingPayload,
  type LoginPairingQrSession,
  type LoginPairingUser,
} from './loginPairingQrApi';
import { StyledQrCode } from '../qr/StyledQrCode';

const API_BASE = (import.meta.env.VITE_API_URL || '/api/v1').replace(/\/$/, '');
const QR_AUTO_REFRESH_INTERVAL_MS = 60_000;
const QR_AUTO_REFRESH_WINDOW_MS = 10 * 60_000;
const QR_MOUSE_IDLE_MS = 5 * 60_000;
const QR_IDLE_CHECK_MS = 15_000;

type PairingState = 'loading' | 'ready' | 'paired' | 'expired' | 'cancelled' | 'error';

type LoginPairingQrCardProps = {
  isMobile?: boolean;
  isHovered?: boolean;
  variant?: 'card' | 'solo';
  cardWidthPx?: number;
  cardHeightPx?: number;
  onEmailFallback: () => void;
  onPaired: (payload: LoginPairingPayload) => void;
  onSessionChange?: (session: {
    sessionId: string | null;
    status: string;
    expiraEm?: string;
    maxExpiraEm?: string | null;
    user?: LoginPairingUser | null;
  } | null) => void;
};

const LOGIN_PAIRING_SESSION_KEY = 'ac_login_pairing_session';

function secondsUntil(expiraEm?: string): number {
  if (!expiraEm) return 0;
  return Math.max(0, Math.ceil((new Date(expiraEm).getTime() - Date.now()) / 1000));
}

function isIsoTimestampAlive(value?: string | null): boolean {
  if (!value) return true;
  const millis = Date.parse(value);
  if (Number.isNaN(millis)) return false;
  return millis > Date.now();
}

function isSessionAlive(session: {
  expiraEm?: string;
  maxExpiraEm?: string | null;
} | null): boolean {
  if (!session) return false;
  return isIsoTimestampAlive(session.expiraEm) && isIsoTimestampAlive(session.maxExpiraEm ?? null);
}

function formatCountdown(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function buildQrUrl(session: LoginPairingQrSession | null): string {
  if (!session) return '';
  return new URL(`/s/${encodeURIComponent(session.sessionId)}`, window.location.origin).toString();
}

const soloTitleButtonBaseStyle: CSSProperties = {
  minHeight: 58,
  padding: '0 26px',
  borderRadius: 18,
  border: '1px solid rgba(255,255,255,0.42)',
  background:
    'linear-gradient(135deg, #4c1d95 0%, #7c3aed 40%, #16a34a 100%)',
  backgroundSize: '190% 190%',
  color: '#ffffff',
  boxShadow:
    'inset 0 1px 0 rgba(255,255,255,0.42), inset 0 -5px 0 rgba(26, 46, 5, 0.24), 0 14px 26px rgba(76, 29, 149, 0.30)',
  textShadow: '0 2px 10px rgba(0,0,0,0.26)',
  transform: 'translateY(0)',
  overflow: 'hidden',
};

const soloTitleButtonHoverStyle: CSSProperties = {
  backgroundPosition: '100% 50%',
  boxShadow:
    'inset 0 1px 0 rgba(255,255,255,0.48), inset 0 -5px 0 rgba(20, 83, 45, 0.22), 0 18px 34px rgba(124, 58, 237, 0.42), 0 0 24px rgba(34, 197, 94, 0.24)',
  transform: 'translateY(-2px)',
};

const subtleTextStyle: CSSProperties = {
  fontSize: '0.62rem',
  color: '#94a3b8',
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
};

export function LoginPairingQrCard({
  isMobile = false,
  isHovered = false,
  variant = 'card',
  cardWidthPx,
  cardHeightPx,
  onEmailFallback,
  onPaired,
  onSessionChange,
}: LoginPairingQrCardProps) {
  const [session, setSession] = useState<LoginPairingQrSession | null>(null);
  const [sessionStatus, setSessionStatus] = useState('PENDING');
  const [state, setState] = useState<PairingState>(() => (isMobile ? 'ready' : 'loading'));
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const pairedRef = useRef(false);
  const autoRefreshStartedAtRef = useRef(Date.now());
  const lastActivityAtRef = useRef(Date.now());
  const lastRefreshAtRef = useRef(0);
  const refreshInFlightRef = useRef(false);
  const [autoRefreshActive, setAutoRefreshActive] = useState(true);

  const storeSession = (next: { sessionId: string | null; status: string; expiraEm?: string; maxExpiraEm?: string | null; user?: LoginPairingUser | null } | null) => {
    onSessionChange?.(next);
    try {
      if (!next?.sessionId) {
        sessionStorage.removeItem(LOGIN_PAIRING_SESSION_KEY);
      } else {
        sessionStorage.setItem(LOGIN_PAIRING_SESSION_KEY, JSON.stringify({
          sessionId: next.sessionId,
          status: next.status,
          expiraEm: next.expiraEm,
          maxExpiraEm: next.maxExpiraEm,
        }));
      }
    } catch {
      // storage indisponivel nao bloqueia o fluxo
    }
  };

  const qrValue = useMemo(() => buildQrUrl(session), [session]);
  const isSolo = variant === 'solo';
  const cardScale = isMobile ? 1 : Math.min(Math.max((cardHeightPx || 228) / 228, 1), isSolo ? 1.7 : 1.35);
  const titleBlockHeight = isMobile ? 44 : Math.round((isSolo ? 52 : 46) * cardScale);
  const titleFontSize = isMobile ? '0.88rem' : `${((isSolo ? 1.25 : 0.85) * cardScale).toFixed(2)}rem`;
  const qrInset = isSolo ? 8 : 0;
  const qrPadding = isSolo ? 16 : 0;
  const qrWidth = Math.max(64, Math.round((cardWidthPx || (isMobile ? 148 : 174)) - qrInset * 2) - qrPadding);
  const qrHeight = Math.max(64, Math.round((cardHeightPx || (isMobile ? 210 : 228)) - titleBlockHeight - qrInset * 2) - qrPadding);
  const qrRenderSize = Math.min(qrWidth, qrHeight);

  const qrIconSize = Math.round(qrRenderSize * (isSolo ? 0.28 : 0.32));

  //#region AC_BLOCK login-pairing.qr-activity-refresh -- Renovacao do QR por atividade
  // @ac-purpose Mantem o QR vivo sem tela negativa: expirou, atividade do usuario gera novo QR.
  // @ac-related react-app/src/pages/auth/LoginPage.tsx#login.qr-auto-login
  // @ac-routes /login; /s/:sessionId
  const hasRecentQrActivity = () => {
    const now = Date.now();
    return (
      autoRefreshActive &&
      now - autoRefreshStartedAtRef.current <= QR_AUTO_REFRESH_WINDOW_MS &&
      now - lastActivityAtRef.current <= QR_MOUSE_IDLE_MS
    );
  };

  const canAutoRefreshQr = () => {
    return hasRecentQrActivity() && Date.now() - lastRefreshAtRef.current >= QR_AUTO_REFRESH_INTERVAL_MS;
  };

  const triggerQrRefresh = () => {
    if (refreshInFlightRef.current) return;
    refreshInFlightRef.current = true;
    setSession(null);
    setSessionStatus('PENDING');
    setState('loading');
    storeSession(null);
    setRefreshKey((key) => key + 1);
  };

  useEffect(() => {
    if (isMobile) {
      pairedRef.current = false;
      refreshInFlightRef.current = false;
      setSession(null);
      setSessionStatus('PENDING');
      setState('ready');
      storeSession(null);
      return;
    }

    let cancelled = false;
    pairedRef.current = false;
    refreshInFlightRef.current = true;
    setState('loading');
    setSession(null);
    setSessionStatus('PENDING');

    const restoreOrCreate = async () => {
      try {
        const storedRaw = sessionStorage.getItem(LOGIN_PAIRING_SESSION_KEY);
        if (storedRaw) {
          const stored = JSON.parse(storedRaw) as { sessionId?: string; expiraEm?: string; maxExpiraEm?: string | null; status?: string };
          if (stored.sessionId) {
            if (!isSessionAlive(stored)) {
              setSession(null);
              setSessionStatus('EXPIRED');
              storeSession(null);
            } else {
              const status = await getLoginPairingStatus(stored.sessionId);
              const resolvedExpiraEm = status.expiraEm || stored.expiraEm || new Date(Date.now() + 30_000).toISOString();
              const resolvedMaxExpiraEm = status.maxExpiraEm ?? stored.maxExpiraEm ?? null;
              const resolvedSession = {
                sessionId: stored.sessionId,
                expiraEm: resolvedExpiraEm,
                maxExpiraEm: resolvedMaxExpiraEm,
                intent: 'login_pairing' as const,
                bondingPath: `/security/bonding?s=${encodeURIComponent(stored.sessionId)}&intent=login_pairing`,
              };

              if (!cancelled && status?.status && status.status !== 'EXPIRED' && status.status !== 'CANCELLED' && isSessionAlive(resolvedSession)) {
                lastRefreshAtRef.current = Date.now();
                setSession(resolvedSession);
                setSessionStatus(status.status);
                setSecondsLeft(secondsUntil(resolvedSession.expiraEm));
                setState('ready');
                storeSession({
                  sessionId: resolvedSession.sessionId,
                  status: status.status,
                  expiraEm: resolvedSession.expiraEm,
                  maxExpiraEm: resolvedSession.maxExpiraEm,
                });
                return;
              }
            }

            setSession(null);
            setSessionStatus('EXPIRED');
            storeSession(null);
          }
        }

        const createdSession = await createLoginPairingQr(getDeviceFingerprint());
        if (cancelled) return;
        if (!isSessionAlive(createdSession)) {
          setSession(null);
          setSessionStatus('EXPIRED');
          setState('expired');
          storeSession(null);
          return;
        }
        lastRefreshAtRef.current = Date.now();
        setSession(createdSession);
        setSessionStatus('PENDING');
        setSecondsLeft(secondsUntil(createdSession.expiraEm));
        setState('ready');
        storeSession({
          sessionId: createdSession.sessionId,
          status: 'PENDING',
          expiraEm: createdSession.expiraEm,
          maxExpiraEm: createdSession.maxExpiraEm ?? null,
        });
      } catch {
        if (cancelled) return;
        setState('error');
      }
    };

    restoreOrCreate()
      .finally(() => {
        if (!cancelled) {
          refreshInFlightRef.current = false;
        }
      });

    return () => {
      cancelled = true;
    };
  }, [refreshKey, isMobile]);

  useEffect(() => {
    if (isMobile) return;

    const registerActivity = () => {
      lastActivityAtRef.current = Date.now();
      if (!autoRefreshActive) {
        autoRefreshStartedAtRef.current = Date.now();
        setAutoRefreshActive(true);
      }
      if (state === 'expired' || state === 'error') {
        triggerQrRefresh();
      }
    };

    window.addEventListener('mousemove', registerActivity, { passive: true });
    window.addEventListener('keydown', registerActivity);
    window.addEventListener('touchstart', registerActivity, { passive: true });

    const idleTimer = window.setInterval(() => {
      if (Date.now() - lastActivityAtRef.current > QR_MOUSE_IDLE_MS) {
        setAutoRefreshActive(false);
      }
    }, QR_IDLE_CHECK_MS);

    return () => {
      window.removeEventListener('mousemove', registerActivity);
      window.removeEventListener('keydown', registerActivity);
      window.removeEventListener('touchstart', registerActivity);
      window.clearInterval(idleTimer);
    };
  }, [autoRefreshActive, isMobile, state]);

  useEffect(() => {
    if (isMobile) return;
    if (!isHovered) return;
    lastActivityAtRef.current = Date.now();
    if (!autoRefreshActive) {
      autoRefreshStartedAtRef.current = Date.now();
      setAutoRefreshActive(true);
    }
    if (state === 'expired' || state === 'error') {
      triggerQrRefresh();
    }
  }, [isHovered, autoRefreshActive, isMobile, state]);

  useEffect(() => {
    if (isMobile) return;
    if (!session || state === 'paired') return;
    const timer = window.setInterval(() => {
      const remaining = secondsUntil(session.expiraEm);
      setSecondsLeft(remaining);
      if (remaining <= 0) {
        if (sessionStatus === 'PENDING' && (canAutoRefreshQr() || hasRecentQrActivity())) {
          triggerQrRefresh();
          return;
        }
        storeSession(null);
        setSession(null);
        setSessionStatus('EXPIRED');
        setState((current) => (current === 'paired' ? current : 'expired'));
      }
    }, 1000);
    return () => window.clearInterval(timer);
  }, [session, sessionStatus, state, autoRefreshActive, isMobile]);

  useEffect(() => {
    if (isMobile) return;
    if (!session || state !== 'ready') return;
    const sse = new EventSource(`${API_BASE}/auth/session-sse/${encodeURIComponent(session.sessionId)}`, { withCredentials: true });

    const finishPairing = (user?: LoginPairingUser | null, login?: LoginPairingPayload['login']) => {
      if (!user || pairedRef.current) return;
      pairedRef.current = true;
      setState('paired');
      storeSession(null);
      onPaired({ user, login });
    };

    sse.addEventListener('login_pairing_claimed', (event) => {
      const payload = JSON.parse((event as MessageEvent).data || '{}');
      getLoginPairingStatus(session.sessionId)
        .then((status) => {
          setSessionStatus(status.status);
          storeSession({
            sessionId: session.sessionId,
            status: status.status,
            expiraEm: status.expiraEm || session.expiraEm,
            maxExpiraEm: status.maxExpiraEm ?? session.maxExpiraEm ?? null,
            user: status.user ?? null,
          });
          finishPairing(payload.user || status.user, status.login);
        })
        .catch(() => finishPairing(payload.user, undefined));
    });

    sse.addEventListener('session_update', () => {
      getLoginPairingStatus(session.sessionId)
        .then((status) => {
          setSessionStatus(status.status);
          storeSession({
            sessionId: session.sessionId,
            status: status.status,
            expiraEm: status.expiraEm || session.expiraEm,
            maxExpiraEm: status.maxExpiraEm ?? session.maxExpiraEm ?? null,
            user: status.user ?? null,
          });
          if (status.status === 'EXPIRED' || status.status === 'CANCELLED') {
            setSession(null);
            storeSession(null);
            if (status.status === 'EXPIRED' && hasRecentQrActivity()) {
              triggerQrRefresh();
              return;
            }
            setState('expired');
            return;
          }
          finishPairing(status.user, status.login);
        })
        .catch(() => undefined);
    });

    const pollTimer = window.setInterval(() => {
      if (pairedRef.current || secondsUntil(session.expiraEm) <= 0) return;
      getLoginPairingStatus(session.sessionId)
        .then((status) => {
          setSessionStatus(status.status);
          storeSession({
            sessionId: session.sessionId,
            status: status.status,
            expiraEm: status.expiraEm || session.expiraEm,
            maxExpiraEm: status.maxExpiraEm ?? session.maxExpiraEm ?? null,
            user: status.user ?? null,
          });
          if (status.status === 'EXPIRED' || status.status === 'CANCELLED') {
            setSession(null);
            storeSession(null);
            if (status.status === 'EXPIRED' && hasRecentQrActivity()) {
              triggerQrRefresh();
              return;
            }
            setState('expired');
            return;
          }
          finishPairing(status.user, status.login);
        })
        .catch(() => undefined);
    }, 4000);

    return () => {
      window.clearInterval(pollTimer);
      sse.close();
    };
  }, [session, state, onPaired, isMobile]);

  const resetQr = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    autoRefreshStartedAtRef.current = Date.now();
    lastActivityAtRef.current = Date.now();
    setAutoRefreshActive(true);
    triggerQrRefresh();
  };
  //#endregion AC_BLOCK login-pairing.qr-activity-refresh

  const openEmail = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onEmailFallback();
  };

  const cancelPendingSession = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (!session?.sessionId) return;

    try {
      await cancelLoginPairingSession(session.sessionId, getDeviceFingerprint());
    } catch {
      // best effort
    }

    setSession(null);
    setSessionStatus('CANCELLED');
    setState('cancelled');
    setAutoRefreshActive(false);
    storeSession(null);
  };

  if (state === 'loading' || state === 'error' || state === 'expired') {
    return renderAddAccountFallback(isMobile, openEmail, isSolo);
  }

  if (state === 'paired') {
    return (
      <>
        <CheckCircle2 size={isSolo ? 68 : isMobile ? 38 : 46} style={{ color: '#16a34a' }} />
        <div style={{ color: '#166534', fontWeight: 800, fontSize: isSolo ? '1.2rem' : '0.84rem' }}>Perfil conectado</div>
        <div style={{ ...subtleTextStyle, fontSize: isSolo ? '0.78rem' : subtleTextStyle.fontSize }}>Conta salva</div>
      </>
    );
  }

  if (state === 'cancelled') {
    return (
      <>
        <div style={{
          width: isSolo ? 104 : isMobile ? 68 : 76,
          height: isSolo ? 104 : isMobile ? 68 : 76,
          borderRadius: '50%',
          background: 'rgba(15, 23, 42, 0.04)',
          border: '2px dashed rgba(15, 23, 42, 0.12)',
          display: 'grid',
          placeItems: 'center',
          color: '#64748b',
        }}>
          <RefreshCw size={isSolo ? 48 : isMobile ? 28 : 34} />
        </div>
        <div style={{ color: '#475569', fontWeight: 700, fontSize: isSolo ? '1.15rem' : '0.85rem' }}>QR cancelado</div>
        <button type="button" onClick={resetQr} style={linkButtonStyle}>
          Gerar novo QR
        </button>
      </>
    );
  }

  if (isMobile || isHovered) {
    return renderAddAccountFallback(isMobile, openEmail, isSolo);
  }

  return (
    <div style={{
      width: '100%',
      height: '100%',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      borderRadius: isSolo ? 24 : 14,
      overflow: 'hidden',
      background: '#ffffff',
      padding: 0,
      boxSizing: 'border-box',
    }}>
      <div style={{
        color: '#ffffff',
        fontWeight: 500,
        fontSize: titleFontSize,
        lineHeight: 1.05,
        padding: isSolo ? '18px 18px 6px' : (isMobile ? '15px 8px' : '12px 8px 4px'),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 0,
        letterSpacing: '-0.01em',
        boxSizing: 'border-box',
        flex: '0 0 auto',
      }}>
        {(!isMobile || isSolo) ? (
          <div
            style={{
              ...soloTitleButtonBaseStyle,
              minHeight: isSolo ? 58 : Math.max(30, Math.round(36 * cardScale)),
              padding: isSolo ? '0 32px' : `0 ${Math.max(12, Math.round(18 * cardScale))}px`,
              borderRadius: isSolo ? 18 : Math.max(8, Math.round(10 * cardScale)),
              border: '1px solid rgba(255,255,255,0.35)',
              boxShadow: isSolo
                ? 'inset 0 1px 0 rgba(255,255,255,0.42), inset 0 -5px 0 rgba(26, 46, 5, 0.24), 0 14px 26px rgba(76, 29, 149, 0.30)'
                : 'inset 0 1px 0 rgba(255,255,255,0.35), inset 0 -3px 0 rgba(26, 46, 5, 0.18), 0 8px 16px rgba(76, 29, 149, 0.18)',
              ...(isHovered ? soloTitleButtonHoverStyle : {}),
              position: 'relative',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'transform 0.28s ease, box-shadow 0.28s ease, background-position 0.65s ease',
            }}
          >
            <span style={{
              position: 'absolute',
              inset: 0,
              transform: isHovered ? 'translateX(115%) skewX(-18deg)' : 'translateX(-115%) skewX(-18deg)',
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.42), transparent)',
              transition: 'transform 0.9s ease',
            }} />
            <span style={{ position: 'relative', zIndex: 1 }}>Adicionar Conta</span>
          </div>
        ) : (
          <span style={{ color: '#334155' }}>Adicionar Conta</span>
        )}
      </div>
      <div style={{
        width: '100%',
        height: '100%',
        flex: 1,
        borderRadius: isSolo ? 22 : 12,
        padding: isSolo ? `${qrInset}px ${qrInset + 4}px ${qrInset + 14}px` : 0,
        boxSizing: 'border-box',
        display: 'grid',
        placeItems: 'center',
        background: '#ffffff',
        overflow: 'hidden',
        position: 'relative',
        zIndex: 1,
      }}>
        {qrValue ? (
          <div style={{
            width: '100%',
            height: '100%',
            display: 'grid',
            placeItems: 'center',
            transform: 'none',
            transformOrigin: 'center',
          }}>
            <StyledQrCode
              value={qrValue}
              size={qrRenderSize}
              width={qrWidth}
              height={qrHeight}
              shape="square"
              style="rounded"
              icon="logo-shield-qrcode-backing"
              iconKind="preset"
              iconSize={qrIconSize}
              hideBackgroundDots={false}
              foreground="#7c3aed"
              background="#ffffff"
              margin={0}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

const linkButtonStyle: CSSProperties = {
  border: 'none',
  background: 'transparent',
  color: '#64748b',
  cursor: 'pointer',
  fontSize: '0.72rem',
  fontWeight: 700,
  padding: 0,
};

function renderAddAccountFallback(
  isMobile: boolean,
  onClick: (event: MouseEvent<HTMLButtonElement>) => void,
  isSolo: boolean,
) {
  return (
    <>
      <div style={{
        width: isSolo ? 132 : isMobile ? 88 : 90,
        height: isSolo ? 132 : isMobile ? 88 : 90,
        borderRadius: '50%',
        background: 'radial-gradient(circle at 35% 28%, #c4b5fd 0%, #7c3aed 42%, #4c1d95 100%)',
        border: '2px dashed rgba(76, 29, 149, 0.36)',
        display: 'grid',
        placeItems: 'center',
        color: '#ffffff',
        boxShadow: '0 16px 34px rgba(124, 58, 237, 0.32), inset 0 1px 0 rgba(255,255,255,0.35)',
      }}>
        <Plus size={isSolo ? 60 : isMobile ? 40 : 42} />
      </div>
      <div style={{
        color: '#0f172a',
        fontWeight: 900,
        fontSize: isSolo ? '1.3rem' : isMobile ? '1.06rem' : '0.95rem',
        marginTop: isSolo ? 28 : isMobile ? 18 : 22,
      }}>
        Adicionar conta
      </div>
      {!isMobile && (
        <button type="button" onClick={onClick} style={linkButtonStyle}>
          Digitar e-mail
        </button>
      )}
    </>
  );
}
